import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import type { RsvpView } from '../api/types';
import { Alert, Field, LoadingBlock } from '../components/ui';
import { formatDate } from '../lib/format';
import { LAGRINGSTID_MAANEDER } from '../lib/lagringstid';
import { RETTIGHETER_STI } from '../lib/gjesterettigheter';
import { Dagsprogram } from './Dagsprogram';
import { GaveListe } from './GaveListe';
import '../styles/onskeliste.css';

/**
 * Offentlig svarside. Gjesten kommer hit fra sin egen lenke og trenger ingen
 * konto – derfor er siden bevisst kort: ja eller nei, allergier, følge, ferdig.
 *
 * Den samme siden brukes til forhåndsvisningen brudeparet ser før de sender ut
 * invitasjonen. Én side, to innganger: skulle forhåndsvisningen vært tegnet for
 * seg, ville den sakte sluttet å ligne på det gjestene faktisk får.
 */
export function RsvpPage({ forhandsvisningFor }: { forhandsvisningFor?: string } = {}) {
  const { token } = useParams<{ token: string }>();
  /** Adressen dataene hentes fra. Forhåndsvisningen har ingen svarlenke. */
  const kilde = forhandsvisningFor ? `/weddings/${forhandsvisningFor}/invitasjon` : `/rsvp/${token}`;
  /**
   * Hvilken fane forhåndsvisningen åpner på.
   *
   * Kommer paret fra ønskelista, vil de se ønskelista – ikke svarskjemaet med
   * ønskelista to klikk unna. Etterpå kan de bla fritt mellom fanene, som
   * gjestene.
   */
  const [adresseparam] = useSearchParams();
  const startfane = adresseparam.get('fane');
  const [view, setView] = useState<RsvpView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Lenken har virket, men gjør det ikke lenger. Det er en annen beskjed. */
  const [utlopt, setUtlopt] = useState<string | null>(null);
  const [attending, setAttending] = useState<boolean | null>(null);
  const [dietary, setDietary] = useState('');
  /*
    Aldri forhåndskrysset, og aldri husket fra forrige svar. Et samtykke
    som ligger der ferdig avkrysset, er ikke gitt – det er antatt, og da
    er det ikke et samtykke etter artikkel 4 nr. 11. Prisen er at den som
    endrer svaret sitt, må krysse av på nytt.
  */
  const [kostholdSamtykke, setKostholdSamtykke] = useState(false);
  /**
   * At gjesten ber om å få fjernet det hun har oppgitt om mat.
   *
   * Finnes fordi feltet ikke lenger kan tømmes for å slette: gjesten ser ikke
   * hva som står der, og et tomt felt betyr derfor «ikke rørt». Å trekke
   * tilbake skal være en handling man gjør med vilje.
   */
  const [fjernKosthold, setFjernKosthold] = useState(false);
  const [plusOneFirstName, setPlusOneFirstName] = useState('');
  const [plusOneLastName, setPlusOneLastName] = useState('');
  const [plusOneDietary, setPlusOneDietary] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justAnswered, setJustAnswered] = useState(false);
  /**
   * Hvilken del av gjestesiden som vises.
   *
   * Menyen har to punkter i dag, men er bygget for flere: her hører også
   * programmet for dagen, kart til lokalet og beskjeder fra brudeparet hjemme.
   */
  const [visning, setVisning] = useState<'svar' | 'program' | 'onskeliste'>(
    startfane === 'onskeliste' || startfane === 'program' ? startfane : 'svar',
  );
  /**
   * Har brudeparet lagt ut et program?
   *
   * Fanen vises bare når det finnes noe å se. En fane som fører til «ingenting
   * her ennå», er et løfte om noe man ikke får.
   */
  const [harProgram, setHarProgram] = useState(false);
  /**
   * Har brudeparet lagt inn noen ønsker?
   *
   * Samme regel som for programmet. Ønskelista er med i Standard og Plus, og
   * et par på Gratis har ingen – da skal ikke gjesten sendes dit etter svaret.
   */
  const [harOnskeliste, setHarOnskeliste] = useState(false);

  useEffect(() => {
    api
      .get<RsvpView>(kilde)
      .then((response) => {
        setView(response);
        // Kostholdsfeltene fylles aldri: svaret bærer bare at noe er oppgitt.
        setPlusOneFirstName(response.guest.plusOneFirstName ?? '');
        setPlusOneLastName(response.guest.plusOneLastName ?? '');
        if (response.guest.rsvpStatus !== 'pending') {
          setAttending(response.guest.rsvpStatus === 'attending');
        }
      })
      .catch((caught) => {
        if (caught instanceof ApiError && caught.code === 'lenke_utlopt') {
          setUtlopt(caught.message);
          return;
        }
        setLoadError(
          caught instanceof ApiError ? caught.message : 'Klarte ikke å hente invitasjonen.',
        );
      });

    // Om det finnes et program, avgjør om fanen skal stå der i det hele tatt.
    // Feiler den, blir fanen borte og resten av siden virker som før – et
    // program er ikke verdt å stoppe en invitasjon for.
    api
      .get<{ items: unknown[] }>(
        forhandsvisningFor ? `/weddings/${forhandsvisningFor}/program` : `/rsvp/${token}/program`,
      )
      .then((svar) => setHarProgram(svar.items.length > 0))
      .catch(() => setHarProgram(false));

    api
      .get<{ items: unknown[] }>(
        forhandsvisningFor
          ? `/weddings/${forhandsvisningFor}/onskeliste`
          : `/rsvp/${token}/onskeliste`,
      )
      .then((svar) => setHarOnskeliste(svar.items.length > 0))
      .catch(() => setHarOnskeliste(false));
  }, [token, kilde, forhandsvisningFor]);

  if (utlopt) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 style={{ fontSize: '1.6rem' }}>Lenken har sluttet å virke</h1>
          <p className="muted">
            {utlopt} Er det noe du lurer på, ta kontakt med brudeparet – de kan lage en ny lenke
            til deg om det trengs.
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 style={{ fontSize: '1.6rem' }}>Denne lenken virker ikke</h1>
          <p className="muted">
            {loadError} Sjekk at du har kopiert hele lenken, eller spør brudeparet om en ny.
          </p>
        </div>
      </div>
    );
  }

  if (!view) return <LoadingBlock label="Henter invitasjonen …" />;

  const { guest, wedding } = view;

  /*
    Er det noe å samtykke til? Bare da vises avkryssingen, og bare da krever
    serveren den. Følgets kosthold teller med: det er like mye en
    helseopplysning, selv om det er noen andres.
  */
  const kostholdOppgitt = !fjernKosthold && Boolean(dietary.trim() || plusOneDietary.trim());
  /** Om det ligger noe om mat fra før – på gjesten selv eller på følget. */
  const kostholdRegistrert = guest.dietaryRegistered || guest.plusOneDietaryRegistered;
  const answered = guest.rsvpStatus !== 'pending';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (attending === null) return;
    // I forhåndsvisningen finnes ingen gjest å svare på vegne av.
    if (forhandsvisningFor) {
      setSubmitError('Dette er en forhåndsvisning. Svaret sendes ikke.');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      const response = await api.post<RsvpView>(`/rsvp/${token}`, {
        attending,
        dietary: attending ? dietary.trim() || null : null,
        kostholdSamtykke,
        fjernKosthold,
        plusOneFirstName: attending ? plusOneFirstName.trim() || null : null,
        plusOneLastName: attending ? plusOneLastName.trim() || null : null,
        plusOneDietary: attending ? plusOneDietary.trim() || null : null,
      });
      setView(response);
      setJustAnswered(true);
      setDietary('');
      setPlusOneDietary('');
      setFjernKosthold(false);
      setKostholdSamtykke(false);
      // Svaret er levert; da er ønskelista det neste gjesten har bruk for –
      // om det finnes en. Ellers står takken alene.
      setVisning(harOnskeliste ? 'onskeliste' : 'svar');
    } catch (caught) {
      setSubmitError(
        caught instanceof ApiError ? caught.message : 'Klarte ikke å sende svaret.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rsvp-page">
      <div className="rsvp-card">
        {/* Står øverst og innenfor kortet, så det følger med om noen tar et
            skjermbilde av forhåndsvisningen for å vise den til noen. */}
        {forhandsvisningFor ? (
          <p className="forhandsvisning-merke">
            Forhåndsvisning · slik gjestene ser invitasjonen. Der det står «Navn
            Navnesen», står gjestens eget navn. Gjestene får menyen når de har svart,
            og «Program» og «Ønskeliste» først når dere har lagt inn noe der. Ingenting
            du gjør her blir lagret.
          </p>
        ) : null}
        <p className="eyebrow" style={{ justifyContent: 'center' }}>
          Du er invitert
        </p>
        <h1 className="rsvp-names">
          {wedding.partner1Name}
          <span className="amp">&amp;</span>
          {wedding.partner2Name}
        </h1>
        <p className="rsvp-meta">
          {formatDate(wedding.weddingDate)}
          {wedding.venue ? (
            <>
              <br />
              {wedding.venue}
              {wedding.city ? `, ${wedding.city}` : ''}
            </>
          ) : null}
        </p>

        <hr className="divider" />

        {/*
          Gjestene får menyen når de har svart, og bare når det er noe mer å se
          enn svaret. I forhåndsvisningen står den alltid, med alle fanene: paret
          skal kunne se seg om uten å måtte fylle ut et skjema først. Merket
          øverst sier fra om forskjellen.
        */}
        {forhandsvisningFor || (answered && (harProgram || harOnskeliste)) ? (
          <nav className="gjestemeny" aria-label="Sider">
            <button
              type="button"
              className={visning === 'svar' ? 'er-valgt' : ''}
              onClick={() => setVisning('svar')}
            >
              Svaret ditt
            </button>
            {harProgram || forhandsvisningFor ? (
              <button
                type="button"
                className={visning === 'program' ? 'er-valgt' : ''}
                onClick={() => setVisning('program')}
              >
                Program
              </button>
            ) : null}
            {harOnskeliste || forhandsvisningFor ? (
              <button
                type="button"
                className={visning === 'onskeliste' ? 'er-valgt' : ''}
                onClick={() => setVisning('onskeliste')}
              >
                Ønskeliste
              </button>
            ) : null}
          </nav>
        ) : null}

        {visning === 'program' && (answered || forhandsvisningFor) ? (
          <Dagsprogram
            token={token!}
            weddingDate={wedding.weddingDate}
            forhandsvisningFor={forhandsvisningFor}
          />
        ) : visning === 'onskeliste' && (answered || forhandsvisningFor) ? (
          <>
            {justAnswered ? (
              <p className="gjest-kvittering">
                {guest.rsvpStatus === 'attending'
                  ? `Takk, ${guest.firstName}! Svaret ditt er registrert.`
                  : 'Takk for at du ga beskjed. Svaret er registrert.'}
              </p>
            ) : null}
            <GaveListe token={token!} forhandsvisningFor={forhandsvisningFor} />
          </>
        ) : justAnswered ? (
          <div className="rsvp-done">
            <span className="big-check" aria-hidden="true">
              {guest.rsvpStatus === 'attending' ? '🥂' : '💐'}
            </span>
            <h2 style={{ fontSize: '1.4rem' }}>
              {guest.rsvpStatus === 'attending'
                ? `Så hyggelig, ${guest.firstName}!`
                : 'Takk for at du ga beskjed'}
            </h2>
            <p className="muted">
              {guest.rsvpStatus === 'attending'
                ? 'Svaret ditt er registrert. Vi gleder oss til å se deg.'
                : 'Vi skjønner at det ikke passer. Svaret er registrert.'}
            </p>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setJustAnswered(false)}
            >
              Endre svaret
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p style={{ fontSize: '1.05rem' }}>
              Hei {guest.firstName}! Kommer du?
              {answered ? (
                <span className="small muted">
                  <br />
                  Du svarte {guest.rsvpStatus === 'attending' ? 'ja' : 'nei'} tidligere. Du kan
                  endre svaret her.
                </span>
              ) : null}
            </p>

            <Alert>{submitError}</Alert>

            <div className="rsvp-answer">
              <button
                type="button"
                className={`rsvp-choice yes${attending === true ? ' selected' : ''}`}
                onClick={() => setAttending(true)}
              >
                <span className="big" aria-hidden="true">
                  🥂
                </span>
                Ja, jeg kommer
              </button>
              <button
                type="button"
                className={`rsvp-choice no${attending === false ? ' selected' : ''}`}
                onClick={() => setAttending(false)}
              >
                <span className="big" aria-hidden="true">
                  💌
                </span>
                Dessverre ikke
              </button>
            </div>

            {attending ? (
              <div style={{ textAlign: 'left' }}>
                <Field label="Allergier eller kosthold">
                  <input
                    className="input"
                    placeholder="Glutenfri, vegetar, nøtteallergi …"
                    value={dietary}
                    disabled={fjernKosthold}
                    onChange={(event) => setDietary(event.target.value)}
                  />
                </Field>

                {guest.plusOneAllowed ? (
                  <>
                    {/*
                      Par og følge er det samme her: en plass til. Har paret
                      alt skrevet inn navnet, er «la stå tomt om du kommer
                      alene» en rar ting å bli møtt med, så hintet følger det
                      som sto der da siden ble åpnet – ikke det gjesten skriver.
                    */}
                    <p className="rsvp-bolk">Den du kommer med</p>
                    <p className="tiny muted rsvp-bolkhint">
                      {guest.plusOneFirstName
                        ? 'Kommer du alene likevel, kan du fjerne navnet.'
                        : 'La stå tomt om du kommer alene.'}
                    </p>
                    <div className="form-grid">
                      <Field label="Fornavn">
                        <input
                          className="input"
                          value={plusOneFirstName}
                          onChange={(event) => setPlusOneFirstName(event.target.value)}
                        />
                      </Field>
                      <Field label="Etternavn">
                        <input
                          className="input"
                          value={plusOneLastName}
                          onChange={(event) => setPlusOneLastName(event.target.value)}
                        />
                      </Field>
                    </div>
                    {/*
                      Følget spiser sin egen mat. Feltet kommer først når det
                      står et navn i feltet over – spør man om allergiene til
                      noen som ikke er der, ser det ut som et krav om å ta med
                      noen.
                    */}
                    {plusOneFirstName.trim() ? (
                      <Field label="Allergier eller kosthold">
                        <input
                          className="input"
                          placeholder="Glutenfri, vegetar, nøtteallergi …"
                          value={plusOneDietary}
                          disabled={fjernKosthold}
                          onChange={(event) => setPlusOneDietary(event.target.value)}
                        />
                      </Field>
                    ) : null}
                  </>
                ) : null}

                {/*
                  Står det noe om mat fra før, får gjesten vite det – men ikke
                  hva. Det er hele poenget: en lenke kan videresendes, og den
                  neste skal ikke kunne lese allergien til den som eier den.

                  Derfor må «fjern» være en avkryssing og ikke et tomt felt. Et
                  felt man ikke ser innholdet i, kan ikke tømmes med vilje.
                */}
                {kostholdRegistrert ? (
                  <>
                    <p className="tiny muted">
                      Du har allerede oppgitt kosthensyn. Skriv dem på nytt hvis du vil endre
                      dem, eller la feltet stå tomt for å beholde det du har sendt inn.
                    </p>
                    <label className="kosthold-samtykke">
                      <input
                        type="checkbox"
                        checked={fjernKosthold}
                        onChange={(event) => setFjernKosthold(event.target.checked)}
                      />
                      <span>Fjern det jeg har oppgitt om mat.</span>
                    </label>
                  </>
                ) : null}

                {/*
                  Samtykket kommer først når det er noe å samtykke til. Å møte
                  alle med en avkryssing om helseopplysninger – også de som
                  bare skal si at de kommer – lærer folk å hake av uten å lese,
                  og da er samtykket verdiløst akkurat når det trengs.
                */}
                {kostholdOppgitt ? (
                  <label className="kosthold-samtykke">
                    <input
                      type="checkbox"
                      checked={kostholdSamtykke}
                      onChange={(event) => setKostholdSamtykke(event.target.checked)}
                    />
                    <span>
                      Jeg samtykker til at opplysninger om allergier og kosthold behandles for å
                      tilrettelegge servering i forbindelse med arrangementet.
                      {plusOneDietary.trim() ? (
                        <>
                          {' '}
                          Har du fylt inn for følget ditt, bekrefter du samtidig at de har sagt
                          det er greit.
                        </>
                      ) : null}{' '}
                      <Link to="/gjesteinformasjon">Les mer om hvordan det brukes og slettes.</Link>
                    </span>
                  </label>
                ) : null}
              </div>
            ) : null}

            {/*
              Personvernsbeskjeden står over knappen, ikke under. Den skal være
              lest før noe sendes, og under knappen leser ingen den.

              Fire linjer, ikke fjorten: hvem som lagrer, hva det brukes til,
              hvem som ser det, og hvor lenge. En vegg av tekst her ville fått
              folk til å la være å svare – og det rammer brudeparet.

              Dette er vår egen informasjonsplikt etter artikkel 13, ikke
              brudeparets: det er vi som er behandlingsansvarlige for det som
              skrives her. Se bruksvilkårene punkt 4.
            */}
            <p className="rsvp-personvern">
              Det du skriver her, lagres av <strong>Vår Dag</strong>, som driver denne siden. Det
              brukes til å holde styr på svaret ditt og planlegge dagen, maten medregnet, og er
              synlig for <strong>{wedding.partner1Name} og {wedding.partner2Name}</strong> og dem de
              har gitt tilgang. Vi sletter det {LAGRINGSTID_MAANEDER} måneder etter bryllupet.{' '}
              {/* Til rettighetene, ikke til erklæringen: den som lurer på hva vi
                  har om henne, skal komme til knappen som svarer på det – ikke
                  til en side som forteller at hun kan sende oss en e-post.
                  Utelates i forhåndsvisningen, der det ikke finnes noe token. */}
              {forhandsvisningFor ? (
                <Link to="/gjesteinformasjon">Hva skjer med opplysningene mine?</Link>
              ) : (
                <Link to={RETTIGHETER_STI(token!)}>Hva har dere om meg?</Link>
              )}
            </p>

            <button
              className="btn btn-primary btn-lg btn-block"
              type="submit"
              disabled={attending === null || busy}
              style={{ marginTop: '0.75rem' }}
            >
              {busy ? 'Sender …' : 'Send svaret'}
            </button>
          </form>
        )}

        <p className="tiny muted" style={{ marginTop: '1.5rem', marginBottom: 0 }}>
          {forhandsvisningFor
            ? 'Hver gjest får sin egen lenke, med sitt eget navn øverst.'
            : 'Svaret ditt går kun til brudeparet. Lenken er personlig – ikke del den videre.'}
        </p>
      </div>
    </div>
  );
}
